/*
 * Model for topic examples
 */

define(["backbone", "messaging/messenger", "underscore", "configuration/properties"], function (
  Backbone,
  Messenger,
  _,
  Properties,
) {
  let TopicExamplesModel = {};

  TopicExamplesModel.topicEntry = Backbone.Model.extend({
    idAttribute: "entryId",
    addSubGroup: function (substGroupMatch) {
      //if a single word sub group, just add it
      if (!substGroupMatch.get("multiWord")) {
        //if the entry has a sub group, remove it first
        if (this.get("substitutionGroupId") > 0) {
          this.removeSubGroup();
        }
        this.set("substitutionGroupId", substGroupMatch.get("substitutionGroupId"));
        this.set("substitutionBegin", true);
      } else {
        //multiword sub group: find the start of the group.  Split the matchingEntry and locate the current entry
        const subEntries = substGroupMatch.get("matchingEntry").split(" ");
        const startIndex = substGroupMatch.get("startIndex");
        //iterate through entries and apply sub group
        for (let i = startIndex; i < startIndex + subEntries.length; i++) {
          const entry = this.collection.at(i);
          //get rid of existing sub group, if applicable
          if (entry.get("substitutionGroupId") > 0) {
            entry.removeSubGroup();
          }
          entry.set("substitutionGroupId", substGroupMatch.get("substitutionGroupId"));
          entry.set("substitutionBegin", i === startIndex);
        }
      }
    },
    defaults: {
      surfaceForm: "",
      isImportant: false,
      isOptional: false,
      substitutionGroupId: 0,
      substitutionBegin: false,
    },
    initializeCustom: function () {
      this.on(
        "change:surfaceForm",
        function () {
          //clear sub group and optional tag if the surface form changes
          this.removeSubGroup();
          this.set("isOptional", false);
        },
        this,
      );
    },
    isCurrentSubGroup: function (substGroupMatch) {
      //check if the group match is a match for the CURRENTLY applied sub group
      const subEntries = substGroupMatch.get("matchingEntry").split(" ");
      //get the start index from the match model
      const startIndex = substGroupMatch.get("startIndex");
      //check to see if the sub group is applied to these entries
      let i = startIndex;
      let entry;
      for (; i < startIndex + subEntries.length; i++) {
        entry = this.collection.at(i);
        if (
          entry.get("substitutionGroupId") !== substGroupMatch.get("substitutionGroupId") ||
          entry.get("substitutionBegin") !== (i === startIndex)
        ) {
          return false;
        }
      }
      if (this.collection.length > i) {
        entry = this.collection.at(i);
        if (entry.get("substitutionGroupId") !== 0 && entry.get("substitutionBegin") === false) {
          return false;
        }
      }

      return true;
    },
    removeSubGroup: function () {
      if (this.get("substitutionGroupId") === 0) {
        //no sub group assign, so nothing to do
        return;
      }
      const isSubBegin = this.get("substitutionBegin");
      let seqOrder = this.get("sequenceOrder");
      const collection = this.collection;
      let currentEntry = this;
      //if the entry has a sub group and is not the beginning entry, go back and remove the group from previous entries
      if (!isSubBegin) {
        while (!currentEntry.get("substitutionBegin")) {
          currentEntry._removeSubGroup();
          currentEntry = collection.at(currentEntry.get("sequenceOrder") - 1);
        }
      }
      //remove the sub group from the current entry
      currentEntry._removeSubGroup();

      //check following entries for the additional entries with this sub group (forming a multiword sub group)
      while (seqOrder < collection.length - 1) {
        seqOrder++;
        currentEntry = collection.at(seqOrder); //get next entry
        if (
          currentEntry.get("substitutionBegin") ||
          currentEntry.get("substitutionGroupId") === 0
        ) {
          break; //stop removing sub groups when the entry is no longer part of the multiword sub group
        }
        currentEntry._removeSubGroup();
      }
    },
    _removeSubGroup: function () {
      //clear group from current entry
      this.set("substitutionGroupId", 0);
      this.set("substitutionBegin", false);
    },
  });

  TopicExamplesModel.topicEntries = Backbone.Collection.extend({
    model: TopicExamplesModel.topicEntry,
    addEntry: function (sequence) {
      //make sure that we won't just insert an entry in the middle of an existing multiword sub group match
      //check that the word before and after the proposed new entry have a sub group and that the following word is not the
      //start of a sub group (which means that it was part of a multiword sub group with the previous entry--no need to check
      //if the sub group Ids match).
      //first make sure that there is both a word before and after the new entry
      if (sequence > 0 && sequence < this.length) {
        const entryBefore = this.at(sequence - 1);
        const entryAfter = this.at(sequence); //this will be the entry after the new entry
        if (
          entryBefore.get("substitutionGroupId") > 0 &&
          entryAfter.get("substitutionGroupId") > 0 &&
          !entryAfter.get("substitutionBegin")
        ) {
          //this was a sub group, so remove the sub group
          entryBefore.removeSubGroup();
        }
      }
      //if @sequence is not at the end of the collection, shift the sequence numbers after @sequence to make room
      if (sequence < this.length) {
        this.each(function (entry) {
          if (entry.get("sequenceOrder") >= sequence) {
            entry.set("sequenceOrder", entry.get("sequenceOrder") + 1);
          }
        });
      }
      const newModel = new TopicExamplesModel.topicEntry({ sequenceOrder: sequence });
      this.add(newModel);
      this.dirty = true;
      this.sort(); //sort collection to place new model in the proper position
      return newModel;
    },
    comparator: function (a, b) {
      return Backbone.numericComparator(a, b, "sequenceOrder");
    },
    initializeCustom: function () {
      this.dirty = false;
      //listen for change events in the collection and set dirty to true
      this.on(
        "change",
        function () {
          this.dirty = true;
        },
        this,
      );
    },
    prepareToSave: function () {
      //remove any empty entries from the collection and adjust the sequenceOrder accordingly
      const emptyEntries = this.where({ surfaceForm: "" });
      for (let i = 0; i < emptyEntries.length; i++) {
        this.removeEntry(emptyEntries[i]);
      }
    },
    removeEntry: function (removedEntry) {
      //if the entry had a sub group applied to it, remove it, making sure to handle if it was a multiword sub group entry
      removedEntry.removeSubGroup();
      const removedSeqOrder = removedEntry.get("sequenceOrder");
      //shift entries after removedSeqOrder down by one
      this.each(function (entry) {
        if (entry.get("sequenceOrder") > removedSeqOrder) {
          entry.set("sequenceOrder", entry.get("sequenceOrder") - 1);
        }
      });
      this.remove(removedEntry);
      this.dirty = true;
    },
  });

  TopicExamplesModel.autocompleteSuppressedPair = Backbone.Model.extend({
    idAttribute: "id",
    defaults: {
      allTouchpointsSuppressed: false,
      allLocalesSuppressed: false,
    },
    parse: function (response) {
      return response;
    },
    toJSON: function (options) {
      return this.constructor.__super__.toJSON.apply(this, arguments);
    },
  });

  TopicExamplesModel.autocompleteSuppressedPairs = Backbone.Collection.extend({
    model: TopicExamplesModel.autocompleteSuppressedPair,
    createNewPair: function () {
      const pair = new TopicExamplesModel.autocompleteSuppressedPair();
      this.add(pair);
      this.dirty = true;
      return pair;
    },
    parse: function (models, options) {
      if (!models || !models.length) {
        return models;
      }
      const result = [];
      options = _.extend({ parse: true }, options);
      for (let i = 0; i < models.length; ++i) {
        result.push(new TopicExamplesModel.autocompleteSuppressedPair(models[i], options));
      }
      return result;
    },
    comparator: function (pair) {
      return [pair.get("touchPointName"), pair.get("languageName")];
    },
  });

  TopicExamplesModel.topicExample = Backbone.Model.extend({
    idAttribute: "exampleId",
    defaults: function () {
      const defaultLocaleId = Messenger.reqres.request("default:response:language");
      return {
        autocompleteSuppressed: false,
        autocompleteSuppressedPairs: new TopicExamplesModel.autocompleteSuppressedPairs(),
        autoCompleteSuppressedLocalesFlag: false,
        autoCompleteSuppressedTouchpointsFlag: false,
        localeId: defaultLocaleId,
        onlyAuthoredLocale: false,
      };
    },
    applyDefaults: function () {
      let updatedExamples = new TopicExamplesModel.topicEntries();
      updatedExamples.url = function () {
        const kbId = Messenger.reqres.request("active:kb");
        return Properties.rootURL + "/" + kbId + "/intents/examples/defaults";
      };
      //override fetch to send the entries collection in the request body and to set the sub groups in the entries collection during the sync
      const _this = this;
      updatedExamples.fetch = function (options) {
        options = options ? _.clone(options) : {};
        if (_.isUndefined(options.parse)) {
          options.parse = true;
        }
        options.success = function (resp) {
          if (resp && resp.intentExampleEntries && resp.intentExampleEntries.length) {
            for (let i = 0; i < resp.intentExampleEntries.length; i++) {
              const entry = _this.entries().at(i);
              if (entry) {
                entry.set("substitutionGroupId", resp.intentExampleEntries[i].substitutionGroupId);
                entry.set("substitutionBegin", resp.intentExampleEntries[i].substitutionBegin);
              }
            }
          }
          _this.trigger("sync");
        };
        //clone the example to not affect the current Model
        const clonedExample = _this.clone();
        //apply the entries array to the examples model
        clonedExample.set("intentExampleEntries", _this.entries());
        return this.sync("update", clonedExample, options); //use 'update' to create PUT and pass the example in the body
      };
      updatedExamples.fetch({ url: updatedExamples.url(), reset: true }); //use the url defined above, not the URL of the example entries we pass

      return this;
    },
    backup: function () {
      this._backup();
      this.onSynced(function () {
        this._backup();
      }, this);
    },
    getExampleText: function () {
      let exampleString = "";
      this.entries().each(function (entry) {
        exampleString += entry.get("surfaceForm") + " ";
      }, this);
      exampleString = exampleString.trimStrict();
      return exampleString;
    },
    entries: function () {
      if (!this.entriesCol) {
        this.entriesCol = new TopicExamplesModel.topicEntries();
      }
      return this.entriesCol;
    },
    pairs: function () {
      if (!this.pairsCol) {
        this.pairsCol = new TopicExamplesModel.autocompleteSuppressedPairs();
      }
      return this.pairsCol;
    },
    isEmpty: function () {
      //an example is empty if there are no entries or the entries' surface forms are all blank
      for (let i = 0; i < this.entries().length; i++) {
        const entry = this.entries().at(i);
        if (entry.get("surfaceForm").length > 0) {
          //an entry has some text, so return false
          return false;
        }
      }
      return true; //none of the entries has text (or there were no entries)
    },
    parse: function (response) {
      //first delete any existing entries collection
      delete this.entriesCol;
      if (response && response.intentExampleEntries) {
        //get the example entry array from the response and create a collection from it
        const entries = this.entries();
        entries.add(response.intentExampleEntries);
      }
      //delete this.autocompleteSuppressedPairs;
      delete this.pairsCol;
      if (response && response.autocompleteSuppressedPairs) {
        //get the autocomplete suppression locale-touchpoint pairs list from the response and create
        //a collection from it
        const pair = this.pairs();
        pair.add(response.autocompleteSuppressedPairs);
      }
      return response;
    },
    restore: function () {
      this.set(this.clonedAttributes);
      this.entriesCol = this.backupTopicEntries;
      this.pairsCol = this.backupSuppressedPairs;
      this.dirty = this.wasDirty;
    },
    saveModel: function (options) {
      //apply the entries collection to the intentExampleEntries attribute
      this._applyEntriesToModel();
      this._applyPairsToModel();
      const modelOptions = this._prepareSaveModelOptions(options);
      if (this._allowSave(options)) {
        if (!this.isNew()) {
          this.url = this.get("self").href; //set example url
        }
        if (this.collection.intentId) {
          this.set("intentId", this.collection.intentId);
        }
        return this.save(null, modelOptions);
      } else {
        //Nothing to save, so just say the save was successful
        modelOptions.success(this, {}, options);
      }
    },
    _allowSave: function () {
      // The current user might not be able to edit the locale associated with this Example
      if (!Messenger.reqres.request("can:edit:locale", { languageId: this.get("localeId") })) {
        return false;
      }
      return this.constructor.__super__._allowSave.apply(this, arguments);
    },
    _applyEntriesToModel: function () {
      //if the entries are dirty, then the example is dirty
      if (this.entries().dirty) {
        this.set("intentExampleEntries", this.entries());
        //the above set should make the model dirty, but just in case it doesn't...
        this.dirty = true;
      }
    },
    _applyPairsToModel: function () {
      if (this.pairs().dirty) {
        this.set("autocompleteSuppressedPairs", this.pairs());
        this.dirty = true;
      }
    },
    _backup: function () {
      //save whether the example was dirty at the time
      this.wasDirty = this.dirty;
      this.clonedAttributes = _.clone(this.attributes);
      //create copies of individual objects in the suppressed TouchPoints and Locales
      this.clonedAttributes.autocompleteSuppressedTouchPoints = _.map(
        this.attributes.autocompleteSuppressedTouchPoints,
        _.clone,
      );
      this.clonedAttributes.autocompleteSuppressedLocales = _.map(
        this.attributes.autocompleteSuppressedLocales,
        _.clone,
      );
      //create a new collection and add cloned models to that collection
      const topicEntries = new TopicExamplesModel.topicEntries();
      this.entries().each(function (entriesModel) {
        topicEntries.add(entriesModel.clone());
      });
      this.backupTopicEntries = topicEntries;
      const suppressedPairs = new TopicExamplesModel.autocompleteSuppressedPairs();
      this.pairs().each(function (pairsModel) {
        suppressedPairs.add(pairsModel.clone());
      });
      this.backupSuppressedPairs = suppressedPairs;
    },
    clone: function () {
      const clonedAttributes = _.clone(this.attributes);
      //create copies of individual objects in the suppressed TouchPoints and Locales
      clonedAttributes.autocompleteSuppressedTouchPoints = _.map(
        this.attributes.autocompleteSuppressedTouchPoints,
        _.clone,
      );
      clonedAttributes.autocompleteSuppressedLocales = _.map(
        this.attributes.autocompleteSuppressedLocales,
        _.clone,
      );
      const clonedEntriesCol = new TopicExamplesModel.topicEntries();
      if (this.entriesCol) {
        this.entriesCol.each((entriesModel) => {
          clonedEntriesCol.add(entriesModel.clone());
        });
      }
      const clonedPairsCol = new TopicExamplesModel.autocompleteSuppressedPairs();
      if (this.pairsCol) {
        this.pairsCol.each((pairsModel) => {
          clonedPairsCol.add(pairsModel.clone());
        });
      }
      const clonedTopicExample = new TopicExamplesModel.topicExample();
      clonedTopicExample.set(clonedAttributes);
      clonedTopicExample.entriesCol = clonedEntriesCol;
      clonedTopicExample.pairsCol = clonedPairsCol;
      return clonedTopicExample;
    },
  });

  TopicExamplesModel.topicExamples = Backbone.Collection.extend({
    model: TopicExamplesModel.topicExample,
    addExample: function () {
      const example = new TopicExamplesModel.topicExample();
      //set entries variable and set empty entry
      example.entries().addEntry(0);
      this.add(example);
      return example;
    },
    addExampleWithText: function (exampleText, localeId) {
      const example = this.addExample();
      if (exampleText && typeof exampleText == "string") {
        const newExampleEntries = example.entries();
        let entry = newExampleEntries.at(0); //get first entry to edit
        //split example text by spaces
        const exampleEntries = exampleText.split(/\s+/);
        for (let i = 0; i < exampleEntries.length; i++) {
          if (i > 0) {
            //create a new entry if needed
            entry = newExampleEntries.addEntry(i);
          }
          entry.set("surfaceForm", exampleEntries[i]);
        }
        example.set("localeId", localeId);
      }
      return example;
    },
    comparator: function (example) {
      //create an example text string from the entries and use that to compare (but compare ignoring case)
      return example.getExampleText().toLowerCase();
    },
  });

  TopicExamplesModel.generateExamples = Backbone.Model.extend({
    defaults: {
      paraphrases: [],
    },
    url: function () {
      const kbId = Messenger.reqres.request("active:kb");
      return Properties.rootURL + "/" + kbId + "/intents/examples/generate";
    },
  });

  return TopicExamplesModel;
});
